import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  TextField,
  Alert,
  Box,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  MergeType as MergeIcon,
  DeleteForever as DeleteIcon,
  Warning as WarningIcon,
  Check as CheckIcon,
  Person as PersonIcon,
  People as FamilyIcon,
  Photo as PhotoIcon,
  Article as ArticleIcon
} from '@mui/icons-material';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { auth } from '../firebase';
import logger from '../utils/logger';

/**
 * Dialog shown when an invited user already has an existing account
 * Allows them to either:
 * 1. Sign in and merge/join the new family
 * 2. Delete old account data and start fresh
 */
const ExistingAccountDialog = ({
  open,
  onClose,
  existingUserData,
  invitationData,
  onMerge,
  onDeleteAndContinue,
  loading
}) => {
  const [mode, setMode] = useState(null); // 'merge' | 'delete'
  const [password, setPassword] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState('');
  const [authenticating, setAuthenticating] = useState(false);

  const handleAuthenticate = async () => {
    if (!password) {
      setError('Please enter your password');
      return;
    }

    setError('');
    setAuthenticating(true);

    try {
      // Verify the password by attempting to sign in
      await signInWithEmailAndPassword(auth, existingUserData.email, password);

      if (mode === 'merge') {
        onMerge(password);
      } else if (mode === 'delete') {
        if (!confirmDelete) {
          setError('Please confirm that you want to delete your existing account data');
          setAuthenticating(false);
          return;
        }
        onDeleteAndContinue(password);
      }
    } catch (err) {
      logger.error('Authentication error:', err);
      if (err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
        setError('Incorrect password. Please try again.');
      } else if (err.code === 'auth/too-many-requests') {
        setError('Too many failed attempts. Please try again later.');
      } else {
        setError(err.message || 'Authentication failed. Please try again.');
      }
    } finally {
      setAuthenticating(false);
    }
  };

  const handleClose = () => {
    setMode(null);
    setPassword('');
    setConfirmDelete(false);
    setError('');
    onClose();
  };

  const renderModeSelection = () => (
    <Box>
      <Typography variant="body1" gutterBottom>
        The email <strong>{existingUserData?.email}</strong> is already registered.
      </Typography>

      {existingUserData?.familyCode && (
        <Alert severity="info" sx={{ my: 2 }}>
          This account is currently a member of family: <strong>{existingUserData.familyCode}</strong>
        </Alert>
      )}

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Choose how you'd like to proceed:
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* Option 1: Sign in and join new family */}
        <Button
          variant="outlined"
          size="large"
          startIcon={<MergeIcon />}
          onClick={() => setMode('merge')}
          sx={{
            justifyContent: 'flex-start',
            textAlign: 'left',
            p: 2,
            borderWidth: 2,
            '&:hover': { borderWidth: 2 }
          }}
        >
          <Box>
            <Typography variant="subtitle1" fontWeight="bold">
              Sign In & Join This Family
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Keep your existing account and join {invitationData?.inviterName}'s family
            </Typography>
          </Box>
        </Button>

        {/* Option 2: Delete and start fresh */}
        <Button
          variant="outlined"
          color="error"
          size="large"
          startIcon={<DeleteIcon />}
          onClick={() => setMode('delete')}
          sx={{
            justifyContent: 'flex-start',
            textAlign: 'left',
            p: 2,
            borderWidth: 2,
            '&:hover': { borderWidth: 2 }
          }}
        >
          <Box>
            <Typography variant="subtitle1" fontWeight="bold">
              Delete Existing Account & Start Fresh
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Remove all existing data and create a new account
            </Typography>
          </Box>
        </Button>
      </Box>
    </Box>
  );

  const renderMergeConfirmation = () => (
    <Box>
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="subtitle2" gutterBottom>
          What will happen:
        </Typography>
        <List dense>
          <ListItem sx={{ py: 0 }}>
            <ListItemIcon sx={{ minWidth: 32 }}>
              <CheckIcon fontSize="small" color="success" />
            </ListItemIcon>
            <ListItemText
              primary="You'll be signed into your existing account"
              primaryTypographyProps={{ variant: 'body2' }}
            />
          </ListItem>
          <ListItem sx={{ py: 0 }}>
            <ListItemIcon sx={{ minWidth: 32 }}>
              <CheckIcon fontSize="small" color="success" />
            </ListItemIcon>
            <ListItemText
              primary={`You'll join ${invitationData?.inviterName || 'the inviter'}'s family`}
              primaryTypographyProps={{ variant: 'body2' }}
            />
          </ListItem>
          {existingUserData?.familyCode && (
            <ListItem sx={{ py: 0 }}>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <WarningIcon fontSize="small" color="warning" />
              </ListItemIcon>
              <ListItemText
                primary={`You'll leave your current family (${existingUserData.familyCode})`}
                primaryTypographyProps={{ variant: 'body2' }}
              />
            </ListItem>
          )}
        </List>
      </Alert>

      <Typography variant="body2" color="text.secondary" gutterBottom>
        Enter your password to confirm:
      </Typography>

      <TextField
        fullWidth
        type="password"
        label="Current Password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        error={!!error}
        helperText={error}
        disabled={authenticating || loading}
        sx={{ mt: 1 }}
        autoFocus
      />
    </Box>
  );

  const renderDeleteConfirmation = () => (
    <Box>
      <Alert severity="error" sx={{ mb: 3 }}>
        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
          Warning: This action cannot be undone!
        </Typography>
        <Typography variant="body2">
          The following data will be permanently deleted:
        </Typography>
      </Alert>

      <List dense sx={{ bgcolor: 'error.lighter', borderRadius: 1, mb: 2 }}>
        <ListItem>
          <ListItemIcon sx={{ minWidth: 40 }}>
            <PersonIcon color="error" />
          </ListItemIcon>
          <ListItemText
            primary="Your profile information"
            secondary={existingUserData?.displayName || existingUserData?.email}
          />
        </ListItem>
        {existingUserData?.familyCode && (
          <ListItem>
            <ListItemIcon sx={{ minWidth: 40 }}>
              <FamilyIcon color="error" />
            </ListItemIcon>
            <ListItemText
              primary="Family membership"
              secondary={`Member of ${existingUserData.familyCode}`}
            />
          </ListItem>
        )}
        <ListItem>
          <ListItemIcon sx={{ minWidth: 40 }}>
            <PhotoIcon color="error" />
          </ListItemIcon>
          <ListItemText primary="Photos and uploaded files" />
        </ListItem>
        <ListItem>
          <ListItemIcon sx={{ minWidth: 40 }}>
            <ArticleIcon color="error" />
          </ListItemIcon>
          <ListItemText primary="Documents and vault contents" />
        </ListItem>
      </List>

      <FormControlLabel
        control={
          <Checkbox
            checked={confirmDelete}
            onChange={(e) => setConfirmDelete(e.target.checked)}
            color="error"
            disabled={authenticating || loading}
          />
        }
        label={
          <Typography variant="body2" color="error">
            I understand that all my existing data will be permanently deleted
          </Typography>
        }
      />

      <Divider sx={{ my: 2 }} />

      <Typography variant="body2" color="text.secondary" gutterBottom>
        Enter your password to confirm deletion:
      </Typography>

      <TextField
        fullWidth
        type="password"
        label="Current Password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        error={!!error}
        helperText={error}
        disabled={authenticating || loading}
        sx={{ mt: 1 }}
        autoFocus
      />
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={authenticating || loading}
    >
      <DialogTitle>
        {mode === null && 'Account Already Exists'}
        {mode === 'merge' && 'Sign In & Join Family'}
        {mode === 'delete' && 'Delete Existing Account'}
      </DialogTitle>

      <DialogContent>
        {error && mode && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {mode === null && renderModeSelection()}
        {mode === 'merge' && renderMergeConfirmation()}
        {mode === 'delete' && renderDeleteConfirmation()}
      </DialogContent>

      <DialogActions>
        {mode === null ? (
          <Button onClick={handleClose}>Cancel</Button>
        ) : (
          <>
            <Button
              onClick={() => {
                setMode(null);
                setPassword('');
                setError('');
                setConfirmDelete(false);
              }}
              disabled={authenticating || loading}
            >
              Back
            </Button>
            <Button
              variant="contained"
              color={mode === 'delete' ? 'error' : 'primary'}
              onClick={handleAuthenticate}
              disabled={authenticating || loading || !password || (mode === 'delete' && !confirmDelete)}
              startIcon={authenticating || loading ? <CircularProgress size={20} /> : null}
            >
              {authenticating || loading
                ? 'Processing...'
                : mode === 'merge'
                  ? 'Sign In & Join'
                  : 'Delete & Continue'
              }
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ExistingAccountDialog;
